<script lang="ts" setup>
import type LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useActivateFirmware } from "~/modules/firmware/composables/useActivateFirmware";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import { FirmwareStatus } from "~/modules/firmware/models/FirmwareStatus.enum";
import ILxcLightRotateCw from "~icons/lxc-light/rotate-cw";

const props = defineProps<{
  businessUnitCode: number;
  firmware: Firmware;
}>();
const emit = defineEmits(["change"]);

const { t } = useI18n();

const [activateConfirmDialogVisible, toggleActivateConfirmVisible] =
  useToggle();

const { activateFirmware, error, isLoading } = useActivateFirmware({
  businessUnitCode: props.businessUnitCode,
});

const buttonVisible = computed<boolean>(
  () => props.firmware?.status === FirmwareStatus.DEACTIVATED,
);

const onActivateFirmware = async () => {
  await activateFirmware(props.firmware.uuid);

  if (error.value) {
    (error.value as LxcError).notify(NotificationKey.saveError);
  } else {
    const newFirmware = { ...props.firmware };
    newFirmware.status = FirmwareStatus.ACTIVATED;
    showNotificationSuccess(t(NotificationKey.saveSuccess));
    emit("change", newFirmware);
  }

  toggleActivateConfirmVisible(false);
};

const confirmActivateFirmware = async (event: MouseEvent) => {
  event?.preventDefault();
  event?.stopPropagation();

  if (props.firmware.uuid) {
    toggleActivateConfirmVisible();
  }
};
</script>

<template>
  <lxc-button
    v-if="buttonVisible"
    html-type="button"
    type="borderless"
    :disabled="isLoading"
    :icon="!isLoading ? ILxcLightRotateCw : undefined"
    :title="t('firmware.actions.activate.label')"
    class="invisible"
    @click.prevent="confirmActivateFirmware"
  >
    <lxc-loader v-if="isLoading" />
  </lxc-button>

  <confirm-modal
    :is-dialog-visible="activateConfirmDialogVisible"
    :title="t('firmware.actions.activate.label')"
    :description="
      t('firmware.actions.activate.description', {
        name: props.firmware?.name ?? '',
      })
    "
    :is-loading="isLoading"
    @cancel="toggleActivateConfirmVisible"
    @confirm="onActivateFirmware"
  />
</template>
<style lang="scss" scoped>
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
